<template>
  <main>
    <div v-if="loading">
      <LoadingSpinner />
    </div>
    <div v-else class="pt-2">
      <div class="card p-2 d-flex flex-row">
        <div class="w-25 input-group input-group-sm p-3">
          <div class="input-group-prepend">
            <span class="input-group-text" id="inputGroup-sizing-sm">{{
              $t("lang.label.client")
            }}</span>
          </div>
          <treeselect
            v-model="client_id"
            :options="clientsForTreeselect"
            :normalizer="normalizeOptions"
            :searchable="true"
            :multiple="false"
            :clearable="true"
            :max-height="300"
            v-bind:placeholder="$t('lang.label.search')"
            @search-change="onClientSearchChange"
            class="form-control inline-table"
          />
        </div>
        <div class="w-50 input-group input-group-sm p-4">
          <div class="input-group-prepend">
            <span class="input-group-text" id="inputGroup-sizing-sm"
              >Email</span
            >
          </div>
          <input type="text" class="form-control" v-model="emailClient" />
        </div>
        <div class="w-25 p-1 ml-auto">
          <div class="col-md-12 d-flex justify-content-end my-2">
            <button
              class="btn btn-success"
              :disabled="loading"
              @click="search()"
            >
              <i class="icon-calendar-check-o mr-2"></i>
              <small>
                {{ $t("lang.label.letsgetstarted") }}
              </small>
            </button>
          </div>
        </div>
      </div>

      <div class="card p-2 my-2" v-if="gdprResult">
        <div
          class="d-flex align-items-center justify-content-between mb-2 border-bottom pb-2"
        >
          <h6 class="mb-0">Business contact information</h6>
          <button
            type="button"
            class="btn btn-link btn-sm p-0 gdpr-copy-btn"
            title="Copy section"
            @click="copyBusinessContactSection"
          >
            <i class="icon-copy"></i>
          </button>
        </div>
        <div
          v-for="key in businessContactKeys"
          :key="'bc-' + key"
          class="d-flex align-items-start gdpr-field-row py-1"
        >
          <div class="flex-grow-1 pr-2 small">
            <span class="text-muted">{{ key }}:</span>
            <span class="ml-1 gdpr-field-value">{{ businessContactDisplay(key) }}</span>
          </div>
          <button
            type="button"
            class="btn btn-link btn-sm p-0 flex-shrink-0 gdpr-copy-btn"
            title="Copy field"
            @click="copyField(key, gdprResult.business_contact[key])"
          >
            <i class="icon-copy"></i>
          </button>
        </div>

        <div
          class="d-flex align-items-center justify-content-between mt-4 mb-2 border-bottom pb-2"
        >
          <h6 class="mb-0">Emails sent information</h6>
          <button
            type="button"
            class="btn btn-link btn-sm p-0 gdpr-copy-btn"
            title="Copy section"
            @click="copyEmailsSentSection"
          >
            <i class="icon-copy"></i>
          </button>
        </div>
        <div
          v-for="(email, idx) in emailsSentList"
          :key="'em-' + idx"
          class="mb-4"
        >
          <div class="font-weight-bold small mb-2">
            Email {{ idx + 1
            }}<span v-if="email.scheduled_date_key" class="text-muted">
              — {{ email.scheduled_date_key }}</span
            >
          </div>
          <div
            v-for="fieldKey in emailDisplayFieldKeys"
            :key="'em-' + idx + '-' + fieldKey"
            class="d-flex align-items-start gdpr-field-row py-1"
          >
            <div class="flex-grow-1 pr-2 small">
              <span class="text-muted">{{ fieldKey }}:</span>
              <template v-if="fieldKey === 'message'">
                <div
                  class="gdpr-email-html mt-1 border rounded p-2 bg-light"
                  v-html="email.message"
                ></div>
              </template>
              <span v-else class="ml-1 gdpr-field-value">{{
                email[fieldKey]
              }}</span>
            </div>
            <button
              type="button"
              class="btn btn-link btn-sm p-0 flex-shrink-0 gdpr-copy-btn align-self-start"
              title="Copy field"
              @click="copyField(fieldKey, email[fieldKey])"
            >
              <i class="icon-copy"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="card p-2 mt-2" v-else-if="emptyAfterSearch">
        <div class="col-sm-12 col-md-10 offset-md-1 text-center">
          <div class="alert alert-primary mx-5 my-2 px-2 py-4">
            <h6>
              <i class="fas fa-exclamation-triangle mr-2"></i>
              No business contact founded for this client or email.
            </h6>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
<script>
import { clientsService, adminService } from "@/_services";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Treeselect from "@riophae/vue-treeselect";

export default {
  name: "GeneralDataProtectionRegulation",
  components: {
    LoadingSpinner,
    Treeselect,
  },
  data() {
    return {
      emailClient: null,
      client_id: null,
      loading: false,
      clients: [],
      gdprResult: null,
      emptyAfterSearch: false,
      clientSearchQuery: "",
      emailDisplayFieldKeys: ["scheduled_date_key", "message"],
    };
  },
  computed: {
    clientsForTreeselect() {
      var q = (this.clientSearchQuery || "").trim().toLowerCase();

      // Si no escribió nada, muestra pocos (evita render masivo)
      if (!q) return this.clients.slice(0, 50);

      // Exige mínimo 2 chars para evitar filtrar en cada tecla
      if (q.length < 2) return [];

      var out = [];
      for (var i = 0; i < this.clients.length; i++) {
        var c = this.clients[i];
        // usamos campo pre-normalizado para filtrar más rápido
        if (c._search && c._search.indexOf(q) !== -1) {
          out.push(c);
          if (out.length >= 200) break; // límite duro
        }
      }
      return out;
    },
    businessContactKeys() {
      if (!this.gdprResult || !this.gdprResult.business_contact) return [];
      return Object.keys(this.gdprResult.business_contact);
    },
    emailsSentList() {
      if (!this.gdprResult || !Array.isArray(this.gdprResult.emails_sent)) {
        return [];
      }
      return this.gdprResult.emails_sent;
    },
  },
  created() {
    this.getClients();
  },
  methods: {
    businessContactDisplay(key) {
      var v = this.gdprResult.business_contact[key];
      if (v === null || v === undefined) return "";
      return String(v);
    },
    normalizeOptions(node) {
      return {
        id: node.id,
        label: node.label,
      };
    },
    onClientSearchChange(query) {
      var self = this;
      if (this.clientSearchTimer) clearTimeout(this.clientSearchTimer);
      this.clientSearchTimer = setTimeout(function () {
        self.clientSearchQuery = query || "";
      }, 200);
    },
    formatKeyValueLine(key, value) {
      var v = value === null || value === undefined ? "" : String(value);
      return key + ": " + v;
    },
    objectToPlainText(obj) {
      var lines = [];
      var keys = Object.keys(obj);
      for (var i = 0; i < keys.length; i++) {
        lines.push(this.formatKeyValueLine(keys[i], obj[keys[i]]));
      }
      return lines.join("\n");
    },
    copyToClipboard(text) {
      var str = text === null || text === undefined ? "" : String(text);
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(str).then(
          function () {
            console.log("[GDPR] copied to clipboard, length:", str.length);
          },
          function () {
            this.copyToClipboardFallback(str);
          }.bind(this)
        );
      }
      this.copyToClipboardFallback(str);
      return Promise.resolve();
    },
    copyToClipboardFallback(text) {
      if (window.clipboardData && window.clipboardData.setData) {
        window.clipboardData.setData("Text", text);
        console.log("[GDPR] copied to clipboard (IE), length:", text.length);
        return;
      }
      if (
        document.queryCommandSupported &&
        document.queryCommandSupported("copy")
      ) {
        var textarea = document.createElement("textarea");
        textarea.textContent = text;
        textarea.style.position = "fixed";
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand("copy");
          console.log("[GDPR] copied to clipboard (execCommand), length:", text.length);
        } catch (ex) {
          console.warn("[GDPR] copy failed.", ex);
        } finally {
          document.body.removeChild(textarea);
        }
      }
    },
    copyField(key, value) {
      this.copyToClipboard(this.formatKeyValueLine(key, value));
    },
    copyBusinessContactSection() {
      if (!this.gdprResult || !this.gdprResult.business_contact) return;
      this.copyToClipboard(
        this.objectToPlainText(this.gdprResult.business_contact)
      );
    },
    copyEmailsSentSection() {
      if (!this.gdprResult || !this.gdprResult.emails_sent) return;
      var emails = this.gdprResult.emails_sent;
      var blocks = [];
      for (var i = 0; i < emails.length; i++) {
        var e = emails[i];
        var slice = {
          scheduled_date_key: e.scheduled_date_key,
          message: e.message,
        };
        blocks.push(
          "--- email " + (i + 1) + " ---\n" + this.objectToPlainText(slice)
        );
      }
      this.copyToClipboard(blocks.join("\n\n"));
    },
    async getClients() {
      this.loading = true;
      try {
        var result = await clientsService.getAll();

        // ✅ sin ?. ni ??
        var list = Array.isArray(result)
          ? result
          : result && result.data
          ? result.data
          : [];

        list = list.filter((client) => client.client_status_id == 4);

        this.clients = (Array.isArray(list) ? list : []).map(function (client) {
          var label = client.name || "";
          return {
            id: client.id,
            label: label,
            _search: String(label).toLowerCase(), // ✅ ayuda al filtro
          };
        });

        // Si quedó un valor inválido seleccionado, límpialo
        if (
          this.client_id != null &&
          !this.clients.some((c) => c.id === this.client_id)
        ) {
          this.client_id = null;
        }
      } finally {
        console.log(this.clients);
        this.loading = false;
      }
    },
    search() {
      if (
        this.client_id == null ||
        this.emailClient == "" ||
        this.emailClient == null
      ) {
        this.$swal({
          type: "error",
          title: this.$t("lang.label.error"),
          text: this.$t("lang.label.must_to_have_client_or_email"),
          showConfirmButton: true,
        });
        return;
      }

      this.loading = true;
      this.emptyAfterSearch = false;
      adminService
        .getProspectCampaignSentEmails(this.client_id, this.emailClient)
        .then((response) => {
          console.log(response);
          this.gdprResult = response;
          this.emptyAfterSearch = false;
        })
        .catch((error) => {
          this.gdprResult = null;
          this.emptyAfterSearch = true;
          if (error && error.message) {
            console.log("[GDPR] search error:", error.message);
          } else {
            console.log("[GDPR] search error:", error);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
<style scoped>
.gdpr-field-row {
  border-bottom: 1px solid #eee;
}
.gdpr-field-row:last-child {
  border-bottom: none;
}
.gdpr-field-value {
  word-break: break-word;
}
.gdpr-email-html {
  max-height: 280px;
  overflow: auto;
  word-break: break-word;
}
.gdpr-copy-btn {
  min-width: 2rem;
  line-height: 1;
}
</style>
